// Google Drive image upload — BYO storage per the §14 architecture decision.
// Uploads go to the *user's* Drive, not our Storage bucket. We get back a public
// URL and store only that reference on the deck.
//
// Scope used: drive.file (only files Prezync creates / user opens via picker).
// API: Drive REST v3 multipart upload. No SDK — keeps the bundle lean.

import { getDriveAccessToken } from './firebase.js';

const UPLOAD_URL = 'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,name,webContentLink,thumbnailLink';
const PERMISSIONS_URL = (id) => `https://www.googleapis.com/drive/v3/files/${id}/permissions`;
const FOLDER_NAME = 'Prezync';

// Cache the Prezync folder ID per session so we don't re-create it on every upload.
let prezyncFolderIdPromise = null;

async function getPrezyncFolderId(token) {
  if (prezyncFolderIdPromise) return prezyncFolderIdPromise;
  prezyncFolderIdPromise = (async () => {
    // Search for an existing folder named "Prezync"
    const q = encodeURIComponent(`mimeType='application/vnd.google-apps.folder' and name='${FOLDER_NAME}' and trashed=false`);
    const res = await fetch(
      `https://www.googleapis.com/drive/v3/files?q=${q}&fields=files(id,name)&pageSize=1`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    if (!res.ok) throw await driveHttpError('Drive search failed', res);
    const data = await res.json();
    if (data.files?.[0]?.id) return data.files[0].id;

    // Not found — create it.
    const create = await fetch('https://www.googleapis.com/drive/v3/files?fields=id,name', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ name: FOLDER_NAME, mimeType: 'application/vnd.google-apps.folder' }),
    });
    if (!create.ok) throw await driveHttpError('Drive folder create failed', create);
    const folder = await create.json();
    return folder.id;
  })();
  return prezyncFolderIdPromise;
}

async function driveHttpError(what, res) {
  const body = await res.text().catch(() => '');
  const err = new Error(`${what} (${res.status}): ${body.slice(0, 300)}`);
  err.status = res.status;
  return err;
}

// Upload an image File or Blob to the user's Drive. Makes it readable by anyone
// with the link. Returns a stable public image URL suitable for `<img src>`.
export async function uploadImageToDrive(file, { onProgress } = {}) {
  if (!file) throw new Error('No file provided.');
  if (!file.type?.startsWith('image/')) throw new Error('Only image files are supported.');

  let token = await getDriveAccessToken();
  let folderId;
  try {
    folderId = await getPrezyncFolderId(token);
  } catch (err) {
    prezyncFolderIdPromise = null;
    // Only an expired token is worth a fresh one; anything else (API disabled,
    // account policy) would fail again, and the popup would be blocked anyway.
    if (err.status !== 401) throw err;
    token = await getDriveAccessToken({ forceRefresh: true });
    prezyncFolderIdPromise = null;
    folderId = await getPrezyncFolderId(token);
  }

  onProgress?.({ phase: 'uploading', percent: 0 });

  // Build multipart body manually (boundaries, metadata + binary).
  const boundary = `----prezync${Date.now().toString(36)}`;
  const metadata = {
    name: file.name || `image-${Date.now()}`,
    parents: [folderId],
    mimeType: file.type,
  };
  const fileBuffer = await file.arrayBuffer();
  const body = buildMultipart(boundary, metadata, file.type, fileBuffer);

  const upRes = await uploadWithProgress(UPLOAD_URL, body, boundary, token, (p) => {
    onProgress?.({ phase: 'uploading', percent: p });
  });
  if (!upRes.ok) {
    const errText = await upRes.text().catch(() => '');
    throw new Error(`Drive upload failed (${upRes.status}): ${errText.slice(0, 200)}`);
  }
  const meta = JSON.parse(upRes.responseText);
  const fileId = meta.id;

  onProgress?.({ phase: 'sharing' });

  // Make the file readable by anyone with the link.
  const permRes = await fetch(PERMISSIONS_URL(fileId), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ type: 'anyone', role: 'reader' }),
  });
  if (!permRes.ok) {
    const errText = await permRes.text().catch(() => '');
    throw new Error(`Could not make file public (${permRes.status}): ${errText.slice(0, 200)}`);
  }

  onProgress?.({ phase: 'done', percent: 100 });

  // Drive image embedding URL formats (as of 2025):
  //   - `uc?export=view&id=...`     — DEPRECATED, often returns HTML virus-scan
  //   - `thumbnail?id=...&sz=w2000` — DEPRECATED in practice: intermittently 403s
  //                                   and returns broken responses for hot-linked
  //                                   <img src> usage. Don't use.
  //   - `lh3.googleusercontent.com/d/<id>=w2000` — Google's CDN-proxied form,
  //                                                works reliably as <img src>.
  return { url: driveImageUrl(fileId), fileId, name: meta.name };
}

// What went wrong with Drive, in words a presenter can act on.
export function friendlyDriveError(err) {
  const msg = String(err?.message || err || '');
  if (msg.includes('popup-closed-by-user')) return 'Sign-in window closed before granting access. Images are stored in your Google Drive — no Google account? Use the URL tab to add an image from a web address.';
  if (msg.includes('popup-blocked')) return 'Browser blocked the sign-in popup. Allow popups for this site.';
  if (/accessNotConfigured|SERVICE_DISABLED|has not been used in project|is disabled/i.test(msg)) {
    return 'The Google Drive API is switched off for this Prezync project, so files can’t be stored yet.';
  }
  if (/Could not make file public/.test(msg)) {
    return 'Your Google account isn’t allowed to share files publicly (common with school or work accounts), so participants couldn’t see the images. Connect a personal Google account instead.';
  }
  if (/storageQuotaExceeded|quota/i.test(msg)) return 'Your Google Drive is full.';
  if (/\b403\b/.test(msg)) return `Google Drive refused the upload. ${msg.slice(0, 160)}`;
  if (/\b401\b/.test(msg)) return 'Google sign-in expired. Try again.';
  if (msg.includes('Network') || msg.includes('Failed to fetch')) return 'Network error during upload — try again.';
  return msg.length > 200 ? msg.slice(0, 200) + '…' : msg;
}

// Build a working hot-linkable image URL for a Drive file. `size` may be `wN`
// for max-width or `sN` for max-dimension (Google's lh3 sizing syntax).
export function driveImageUrl(fileId, { size = 'w2000' } = {}) {
  return `https://lh3.googleusercontent.com/d/${fileId}=${size}`;
}

// Direct-content URL for non-image Drive files (audio/video). The lh3 CDN
// form is image-only; this serves raw bytes and works as <audio>/<video> src.
// Fine for small narration clips; large files (>~100MB) can hit a virus-scan
// interstitial, but per-slide narration is well under that.
export function driveFileUrl(fileId) {
  return `https://drive.usercontent.google.com/download?id=${fileId}&export=download`;
}

// Upload an audio File/Blob (recorded narration) to the user's Drive and make
// it link-readable. Returns { url, fileId, name } with a playable src URL.
export async function uploadAudioToDrive(file, { onProgress } = {}) {
  if (!file) throw new Error('No file provided.');
  const type = file.type || 'audio/webm';
  if (!type.startsWith('audio/')) throw new Error('Only audio files are supported.');

  let token = await getDriveAccessToken();
  let folderId;
  try {
    folderId = await getPrezyncFolderId(token);
  } catch {
    token = await getDriveAccessToken({ forceRefresh: true });
    prezyncFolderIdPromise = null;
    folderId = await getPrezyncFolderId(token);
  }

  onProgress?.({ phase: 'uploading', percent: 0 });

  const boundary = `----prezync${Date.now().toString(36)}`;
  const metadata = {
    name: file.name || `narration-${Date.now()}.webm`,
    parents: [folderId],
    mimeType: type,
  };
  const fileBuffer = await file.arrayBuffer();
  const body = buildMultipart(boundary, metadata, type, fileBuffer);

  const upRes = await uploadWithProgress(UPLOAD_URL, body, boundary, token, (p) => {
    onProgress?.({ phase: 'uploading', percent: p });
  });
  if (!upRes.ok) {
    const errText = await upRes.text().catch(() => '');
    throw new Error(`Drive upload failed (${upRes.status}): ${errText.slice(0, 200)}`);
  }
  const meta = JSON.parse(upRes.responseText);
  const fileId = meta.id;

  onProgress?.({ phase: 'sharing' });
  const permRes = await fetch(PERMISSIONS_URL(fileId), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ type: 'anyone', role: 'reader' }),
  });
  if (!permRes.ok) {
    const errText = await permRes.text().catch(() => '');
    throw new Error(`Could not make file public (${permRes.status}): ${errText.slice(0, 200)}`);
  }

  onProgress?.({ phase: 'done', percent: 100 });
  return { url: driveFileUrl(fileId), fileId, name: meta.name };
}

// Heal stored image URLs that point at the old/broken Drive endpoints. Called
// at render time so existing decks (whose Firestore docs still hold the dead
// `drive.google.com/thumbnail?...` form) display correctly without a migration.
// Returns the original string when no rewrite is needed.
export function normalizeDriveImageUrl(url) {
  if (!url || typeof url !== 'string') return url;
  if (url.includes('lh3.googleusercontent.com')) return url;
  // Old thumbnail endpoint: drive.google.com/thumbnail?id=<id>&sz=w2000
  const m1 = url.match(/drive\.google\.com\/thumbnail\?(?:[^#]*&)?id=([A-Za-z0-9_-]+)/);
  if (m1) return driveImageUrl(m1[1]);
  // Old uc?export=view&id=<id>
  const m2 = url.match(/drive\.google\.com\/uc\?(?:[^#]*&)?(?:export=view&)?id=([A-Za-z0-9_-]+)/);
  if (m2) return driveImageUrl(m2[1]);
  // Shared "open" link: drive.google.com/open?id=<id>
  const m4 = url.match(/drive\.google\.com\/open\?(?:[^#]*&)?id=([A-Za-z0-9_-]+)/);
  if (m4) return driveImageUrl(m4[1]);
  // Drive file viewer URL: /file/d/<id>/view
  const m3 = url.match(/drive\.google\.com\/file\/d\/([A-Za-z0-9_-]+)/);
  if (m3) return driveImageUrl(m3[1]);
  return url;
}

function buildMultipart(boundary, metadata, mimeType, fileBuffer) {
  const enc = new TextEncoder();
  const head = enc.encode(
    `--${boundary}\r\n` +
    `Content-Type: application/json; charset=UTF-8\r\n\r\n` +
    `${JSON.stringify(metadata)}\r\n` +
    `--${boundary}\r\n` +
    `Content-Type: ${mimeType}\r\n\r\n`
  );
  const tail = enc.encode(`\r\n--${boundary}--`);
  const blob = new Blob([head, fileBuffer, tail]);
  return blob;
}

// fetch() doesn't expose upload progress; XHR does. Fall back to XHR for the
// upload step so we can stream a percent.
function uploadWithProgress(url, body, boundary, token, onPct) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.setRequestHeader('Content-Type', `multipart/related; boundary=${boundary}`);
    xhr.upload.onprogress = (e) => {
      if (!e.lengthComputable) return;
      onPct?.(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, responseText: xhr.responseText, text: () => Promise.resolve(xhr.responseText) });
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.send(body);
  });
}
